"use client"

import parse from "html-react-parser"
import { useEffect, useState } from "react"

import LoadingIcon from "@/components/global/icons/LoadingIcon"
import Combobox from "@/components/ui/combobox"
import { Field } from "@/components/ui/field"
import { cn } from "@/lib/utils"

export type SelectorItem = {
  value: string
  title: string
  triggerTitle?: string
  subtitle: string
  tag: string
  status: string
}

type Props = {
  className?: string
  fullWidth?: boolean
  title?: string
  initialValue?: string | number | null
  value?: string | number | null
  alertText?: string
  placeholder?: string
  error?: string
  items: SelectorItem[]
  onChange: (value: string) => void
  all?: boolean
  allLabel?: string
  loading?: boolean
  loadingText?: string
  hasMore?: boolean
  inline?: boolean

  triggerUIItems?: {
    displaySubtitle?: boolean
    displayTag?: boolean
    displayStatus?: boolean
  }
}

const Selector = ({
  className,
  fullWidth = false,
  initialValue,
  value,
  alertText,
  title,
  onChange,
  all = false,
  allLabel = "Alla",
  placeholder,
  error,
  loading = false,
  loadingText = "Hämtar anläggningar...",
  hasMore = false,
  inline = false,
  triggerUIItems = {
    displaySubtitle: true,
    displayTag: true,
    displayStatus: false
  },
  ...props
}: Props) => {
  const [items, setItems] = useState<SelectorItem[]>(props.items || [])
  const [selectedItem, setSelectedItem] = useState<SelectorItem | undefined>(
    items?.find((c) => c?.value === initialValue)
  )
  const handleOnChange = (value: string) => {
    if (value.startsWith("__")) {
      return
    }
    setSelectedItem(items.find((c) => c?.value === value))
    onChange(value)
  }

  useEffect(() => {
    let newItems = [...props.items]

    if (all === true) {
      const allItem = {
        value: "all",
        title: allLabel,
        subtitle: "",
        tag: "",
        status: ""
      }

      const hasAllItem = newItems.some((item) => item.value === "all")
      if (!hasAllItem) {
        newItems = [allItem, ...newItems]
      }
    }

    // Remove any existing sentinel rows
    const loadingValue = "__loading__"
    newItems = newItems.filter((i) => i.value !== loadingValue)
    // Append a loading row as the last item when loading
    if (loading || hasMore) {
      newItems = [
        ...newItems,
        {
          value: loadingValue,
          title: loadingText,
          subtitle: "",
          tag: "",
          status: ""
        }
      ]
    }

    setItems(newItems)

    // Determine next selected item
    let nextSelected: SelectorItem | undefined
    let controlledValueNotFound = false

    // If we have a controlled value prop, use that
    const controlledValue = value !== undefined ? value : initialValue

    if (controlledValue !== undefined) {
      const foundItem = newItems.find((c) => c?.value === `${controlledValue}`)
      if (foundItem) {
        nextSelected = foundItem
      } else {
        // Controlled value not found in items list - use fallback
        controlledValueNotFound = true
        nextSelected =
          newItems.find((c) => c.value !== "__loading__") || newItems[0]
      }
    } else {
      const stillExists = newItems.find((c) => c?.value === selectedItem?.value)
      if (stillExists && stillExists.value !== "__loading__") {
        nextSelected = stillExists
      } else {
        nextSelected =
          newItems.find((c) => c.value !== "__loading__") || newItems[0]
      }
    }

    // Only update when value actually changes to avoid update loops
    if (nextSelected?.value !== selectedItem?.value) {
      setSelectedItem(nextSelected)

      // If controlled value wasn't found and we're using a fallback,
      // propagate the change via onChange to keep global state in sync
      if (
        controlledValueNotFound &&
        nextSelected &&
        nextSelected.value !== "__loading__"
      ) {
        onChange(nextSelected.value)
      }
    }
  }, [
    props.items,
    all,
    allLabel,
    initialValue,
    value,
    loading,
    loadingText,
    hasMore,
    selectedItem?.value,
    onChange
  ])

  const template = (item: SelectorItem, index: number, isTrigger = false) => {
    if (item.value === "__loading__") {
      return (
        <div className="grow w-full" key={`${index}-${item.value}`}>
          <div className="flex flex-row items-center gap-3 py-2">
            <LoadingIcon className="w-4 h-4 animate-spin" />
            <span className="text-sm text-black-400 mt-1">{loadingText}</span>
          </div>
        </div>
      )
    }

    return (
      <div
        className={cn(
          "grow w-full min-h-[46px] flex flex-row items-center justify-between min-w-0 flex-1 overflow-hidden"
        )}
        key={`${index}-${item.value}`}>
        <div className="flex flex-col justify-start min-w-0 flex-1 overflow-hidden">
          <span
            className={cn("text-base whitespace-nowrap truncate text-left", {
              "font-medium": !isNaN(Number(item.value)) || !isTrigger
            })}>
            {isTrigger ? item.triggerTitle || item.title : item.title}
          </span>
          {((triggerUIItems?.displaySubtitle && isTrigger) || !isTrigger) &&
            item.subtitle && (
              <span className="text-sm text-left whitespace-nowrap truncate">
                {parse(item.subtitle)}
              </span>
            )}
        </div>
        <div className="flex flex-col justify-end flex-shrink-0 ml-4">
          {((triggerUIItems?.displayTag && isTrigger) || !isTrigger) &&
            item?.tag && (
              <span className="badge-contract-consumption bg-black-200 text-black-400 text-sm p-2 rounded-lg font-bold leading-none">
                {item.tag}
              </span>
            )}
          {((triggerUIItems?.displayStatus && isTrigger) || !isTrigger) &&
            item.status && (
              <span
                className={cn(
                  "text-sm transform translate-y-1 block text-right",
                  {
                    "text-green": item.status === "Aktiv",
                    "text-black-400": item.status !== "Aktiv"
                  }
                )}>
                {item.status}
              </span>
            )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        "contractSelector-component flex flex-col md:flex-row justify-between flex-wrap mb-6",
        className
      )}>
      {title && <h2 className="my-auto py-1">{title}</h2>}
      <Field
        label=""
        error={error}
        touched={!!error}
        dirty={!!error}
        displayIcons={!inline}
        className={cn({
          "order-2 sm:order-4 md:w-1/2 lg:w-5/12 xl:w-1/4 max-w-full": title,
          "w-full": fullWidth
        })}>
        <Combobox
          displayChevron={!error}
          className={cn({
            "h-16": !inline,
            "w-full": fullWidth,
            "text-red-500": !!error
          })}
          inline={inline}
          options={[
            ...items.map((item, index) => ({
              value: item.value,
              label: item.title,
              triggerTemplate: template(item, index, true),
              dropdownTemplate: template(item, index),
              disabled: item.value.startsWith("__")
            }))
          ]}
          placeholder={placeholder || "Sök på adress/anläggnings-ID"}
          value={selectedItem?.value ? `${selectedItem?.value}` : undefined}
          onChange={(value) => handleOnChange(value)}
          alertText={
            alertText || "Avslutade anläggningar försvinner efter ett år."
          }
          noResultsMessage="Det finns ingen anläggning som matchar ditt urval. Avslutade anläggningar försvinner efter ett år."
        />
      </Field>
    </div>
  )
}

export default Selector
